import React, { useEffect, useState } from "react";
import { Button, Checkbox, Error, formatNumber } from "@narmi/design_system";
import { ContextForm } from "cerulean";
import PropTypes from "prop-types";
import { useLocalization } from "@fluent/react";
import Feature, { featureEnabled, featureEquals } from "byzantine/src/Feature";
import { MODEL_STR } from "byzantine/src/Role";
import utils from "../../utils";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import AccountCheckList from "../AccountCheckList";
import SectionCard from "../SectionCard";
import AmountTextInput from "../form/AmountTextInput";
import SubUserLimitsDialog from "./SubUserLimitsDialog";

const hasACHPassthroughLimits = (features, sendLimitUsers, collectLimitUsers) =>
  Boolean(features.limits?.ach_passthrough_send) ||
  Boolean(features.limits?.ach_passthrough_collect) ||
  Boolean(sendLimitUsers?.length) ||
  Boolean(collectLimitUsers?.length);

export default function OrganizationAccessLevelExtraPermissions({
  error,
  features,
  formData,
  onChange,
  isUsingConsumerBillpay,
  organizationUsers,
}) {
  if (formData.accessLevel !== "Collaborator") return null;
  const { l10n } = useLocalization();

  const [isLimitsDialogOpen, setIsLimitsDialogOpen] = useState(false);

  const toggleLimitsDialog = () => {
    setIsLimitsDialogOpen(!isLimitsDialogOpen);
  };

  const fedwireTemplatesEnabled = featureEquals(
    features,
    "wire_templates",
    "fedwire"
  );
  const achTemplatesEnabled = featureEnabled(features, {
    or: ["ach_templates"],
  });

  const internationalWireTemplatesEnabled = featureEnabled(features, {
    or: ["international_wire_templates"],
  });

  const businessBillpayEnabled =
    featureEnabled(features, { or: ["bill_pay"] }) && !isUsingConsumerBillpay;

  const subUsersWithACHLimits = organizationUsers?.filter(
    (user) => user.role.limits?.ach_push?.[1] > 0
  );
  const subUsersWithWireLimits = organizationUsers?.filter(
    (user) => user.role.limits?.wire?.[1] > 0
  );
  const subUsersWithUnverifiedACHLimits = organizationUsers?.filter(
    (user) => user.role.limits?.unverified_ach_push?.[1] > 0
  );
  const subUsersWithInternationalWireLimits = organizationUsers?.filter(
    (user) => user.role.limits?.international_wire?.[1] > 0
  );
  const subUsersWithUnverifiedFednowLimits = organizationUsers?.filter(
    (user) => user.role.limits?.fednow_unverified?.[1] > 0
  );
  const subUsersWithCollectACHLimits = organizationUsers?.filter(
    (user) => user.role.limits?.unverified_ach_pull?.[1] > 0
  );
  const subUsersWithACHPassthroughSendLimits = organizationUsers?.filter(
    (user) => user.role.limits?.ach_passthrough_send?.[1] > 0
  );
  const subUsersWithACHPassthroughCollectLimits = organizationUsers?.filter(
    (user) => user.role.limits?.ach_passthrough_collect?.[1] > 0
  );

  // do we have the proper permissions for sub user limits
  const subUserLimitsPermission = features.business_subuser_limits;

  const domesticWireImportEnabled = features.domestic_wire_file_import;

  // if there are no external accounts and the user does not have permission to add an external account,
  // then we SHOULD NOT display the ACH limits field, but if they do have permission to add an external account,
  // then we SHOULD display the ACH limits field
  const addExternalAccountsWithNoAccounts = () => {
    if (!subUserLimitsPermission) return false;
    if (
      !formData.externalAccounts.length &&
      formData[MODEL_STR.EXTERNAL_ACCOUNT]
    ) {
      return true;
    }
    if (
      !formData.externalAccounts.length &&
      !formData[MODEL_STR.EXTERNAL_ACCOUNT]
    ) {
      return false;
    }
    return true;
  };

  const [displayACHLimits, setDisplayACHLimits] = useState(
    addExternalAccountsWithNoAccounts
  );

  // as accounts are checked/unchecked, we need to update the values of any affected permissions
  useEffect(() => {
    if (!formData[MODEL_STR.ACCOUNT]) {
      const update = {};
      OrganizationAccessLevelExtraPermissions.affectedPermissionsRequiringAllAccounts.forEach(
        (model) => {
          update[model] = false;
        }
      );
      onChange(update);
    }
  }, [formData.accounts, formData[MODEL_STR.ACCOUNT]]);

  const [displayAddExternalAccount, setDisplayAddExternalAccount] = useState(
    utils.allAreSelected(formData.externalAccounts) &&
      formData[MODEL_STR.ACH_TRANSFER]
  );

  // as externalAccounts are checked/unchecked, we need to update the values of any affected permissions
  // if make external transfers is unchecked, then we need to uncheck the add external account permission
  useEffect(() => {
    if (
      !utils.allAreSelected(formData.externalAccounts) ||
      !formData[MODEL_STR.ACH_TRANSFER]
    ) {
      setDisplayAddExternalAccount(false);
      onChange({ [MODEL_STR.EXTERNAL_ACCOUNT]: false });
    } else {
      setDisplayAddExternalAccount(true);
    }
  }, [formData[MODEL_STR.ACH_TRANSFER], formData.externalAccounts]);

  // if the org has no external accounts and we don't have permission to add an external account,
  // then we want to update the display for the ach limits field
  useEffect(() => {
    if (!formData.externalAccounts.length && subUserLimitsPermission) {
      setDisplayACHLimits(formData[MODEL_STR.EXTERNAL_ACCOUNT]);
    }
  }, [formData[MODEL_STR.EXTERNAL_ACCOUNT]]);

  const validateACHLimits = (value, allFields) => {
    // sub-user ach limits must be less than or equal to the organization's daily ACH Push limits
    // sub-user ach limits can only be set if an external account is selected
    if (allFields.accessLevel !== "Collaborator") return null;
    const subUserLimits = utils.parseValueAsFloat(value);
    const hasSelectedExternalAccount = allFields.externalAccounts.some(
      (a) => a.selected
    );
    const canMakeExternalTransfers = allFields[MODEL_STR.ACH_TRANSFER];
    if (subUserLimits <= 0 && canMakeExternalTransfers) {
      return "Daily limit must be greater than $0";
    }
    // If there are no external accounts linked to the organization & the user does not have permission to
    // add an external account, then the user should not be able to set a limit.
    if (
      subUserLimits &&
      !formData.externalAccounts.length &&
      !allFields[MODEL_STR.EXTERNAL_ACCOUNT]
    ) {
      return "Must have ability to add account to set a limit";
    }
    if (
      subUserLimits &&
      !hasSelectedExternalAccount &&
      formData.externalAccounts.length
    )
      return "Must select at least one account to set a limit";
    // only need to check the daily ach_push limit
    const orgDailyLimits = features.limits?.ach_push?.[1];
    if (orgDailyLimits && subUserLimits > orgDailyLimits)
      return `Must be less than or equal to ${formatNumber(
        orgDailyLimits,
        "currency"
      )}`;
    return null;
  };

  const validateWireLimits = (value, allFields) => {
    // sub-user wire limits must be less than or equal to the organization's daily wire limits
    if (allFields.accessLevel !== "Collaborator") return null;
    const subUserLimits = utils.parseValueAsFloat(value);
    const canMakeWireTransfers =
      allFields[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] ||
      allFields[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] ||
      allFields[MODEL_STR.DOMESTIC_WIRE_IMPORT];
    if (subUserLimits <= 0 && canMakeWireTransfers) {
      return "Daily limit must be greater than $0";
    }
    // only need to check the daily wire limit
    const orgDailyLimits = features.limits?.wire?.[1];
    if (orgDailyLimits && subUserLimits > orgDailyLimits) {
      return `Must be less than or equal to ${formatNumber(
        orgDailyLimits,
        "currency"
      )}`;
    }
    return null;
  };

  const validateACHPaymentLimits = (
    value,
    allFields,
    modelString,
    orgDailyLimits
  ) => {
    if (allFields.accessLevel !== "Collaborator") {
      return null;
    }

    const subUserLimits = utils.parseValueAsFloat(value);
    const canMakeACHPayment = allFields[modelString];
    if (subUserLimits <= 0 && canMakeACHPayment) {
      return "Daily limit must be greater than $0";
    }

    // sub user ACH payment limits must be less than or equal to the organization's daily ACH payment limits
    if (orgDailyLimits && subUserLimits > orgDailyLimits) {
      return `Must be less than or equal to ${formatNumber(
        orgDailyLimits,
        "currency"
      )}`;
    }

    return null;
  };

  const validateFedNowSendLimits = (
    value,
    allFields,
    fednowSendDailyLimits
  ) => {
    if (allFields.accessLevel !== "Collaborator") {
      return null;
    }

    const subUserLimits = utils.parseValueAsFloat(value);
    if (subUserLimits <= 0) {
      return "Daily limit must be greater than $0";
    }
    // sub user FedNow Send Payment limits must be less than or equal to the organization's daily FedNow Send Payment limits
    if (fednowSendDailyLimits && subUserLimits > fednowSendDailyLimits) {
      return `Must be less than or equal to ${formatNumber(
        fednowSendDailyLimits,
        "currency"
      )}`;
    }

    return null;
  };

  const doesOrgOrSubUserWireLimitsExist =
    features.limits?.wire || Boolean(subUsersWithWireLimits?.length);
  const doesOrgOrSubUserACHLimitsExist =
    features.limits?.ach_push || Boolean(subUsersWithACHLimits?.length);
  const doesOrgOrSubUserACHPaymentLimitsExist =
    features.limits?.unverified_ach_push ||
    Boolean(subUsersWithUnverifiedACHLimits?.length);
  const doesOrgOrSubUserInternationalWireLimitsExist =
    features.limits?.international_wire ||
    Boolean(subUsersWithInternationalWireLimits?.length);
  const doesOrgOrSubUserACHPassthroughLimitsExist = hasACHPassthroughLimits(
    features,
    subUsersWithACHPassthroughSendLimits,
    subUsersWithACHPassthroughCollectLimits
  );

  const displayLimitsDialog =
    doesOrgOrSubUserACHLimitsExist ||
    doesOrgOrSubUserACHPaymentLimitsExist ||
    doesOrgOrSubUserWireLimitsExist ||
    doesOrgOrSubUserInternationalWireLimitsExist ||
    doesOrgOrSubUserACHPassthroughLimitsExist;

  const canSendACHPayments =
    formData[MODEL_STR.ACH_PAYMENT] ||
    formData[MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE] ||
    formData[MODEL_STR.ACH_PAYMENT_VIA_IMPORT];
  const canCollectACHPayments =
    formData[MODEL_STR.COLLECT_ACH_PAYMENT] ||
    formData[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE] ||
    formData[MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT];

  return (
    <div className="padding--top--l">
      <SectionCard isLoading={false} paddingSize={null}>
        <div className="padding--x--l padding--top--l">
          <SectionCard.Title
            text="Permissions"
            button={
              subUserLimitsPermission && (
                <Feature
                  features={features}
                  or={[
                    "wires",
                    "ach",
                    "ach_payments",
                    "international_wires",
                    "ach_nacha_passthrough",
                  ]}
                >
                  {displayLimitsDialog && (
                    <Button
                      kind="plain"
                      label={l10n.getString("manage-users-view-limits")}
                      testId="view-sub-user-limits-button"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleLimitsDialog();
                      }}
                    />
                  )}
                </Feature>
              )
            }
          />
        </div>
        {!!formData[MODEL_STR.ACCOUNT] && (
          <h5 className="title-style fontWeight--bold">Money Movement</h5>
        )}
        <div className="permission-check-list">
          <Feature features={features} flag="internal_transfers">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.INTRA_INSTITUTION_TRANSFER}
                  label={l10n.getString(
                    "manage-users-intra-institution-transfer"
                  )}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <Feature features={features} flag="ach_payments">
            <div className="inverted-primary-color-checkbox">
              <div
                className="alignChild--left--center"
                style={{ gap: "var(--space-xxs)" }}
              >
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    checked={canSendACHPayments}
                    onChange={() => {
                      const isCurrentlyChecked = canSendACHPayments;
                      onChange(
                        achTemplatesEnabled
                          ? {
                              [MODEL_STR.ACH_PAYMENT]: !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE]:
                                !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_VIA_IMPORT]:
                                features.ach_file_import
                                  ? !isCurrentlyChecked
                                  : false,
                            }
                          : {
                              [MODEL_STR.ACH_PAYMENT]: !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_VIA_IMPORT]:
                                features.ach_file_import
                                  ? !isCurrentlyChecked
                                  : false,
                            }
                      );
                    }}
                    label={l10n.getString("manage-users-ach-payment")}
                  />
                </ContextForm.Field>
              </div>
              {canSendACHPayments &&
                (achTemplatesEnabled || features.ach_file_import) && (
                  <>
                    {achTemplatesEnabled && (
                      <div
                        className="inverted-primary-color-checkbox external-account-checklist"
                        style={{ borderTop: "none" }}
                      >
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <Checkbox
                            field={MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE}
                            label={l10n.getString(
                              "manage-users-ach-payment-with-templates"
                            )}
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                    <div
                      className="inverted-primary-color-checkbox external-account-checklist"
                      style={{ borderTop: "none", paddingBottom: "0px" }}
                    >
                      <ContextForm.Field style={{ marginBottom: "0px" }}>
                        <Checkbox
                          field={MODEL_STR.ACH_PAYMENT}
                          label={l10n.getString(
                            "manage-users-ach-payment-without-templates"
                          )}
                        />
                      </ContextForm.Field>
                    </div>
                  </>
                )}
              {canSendACHPayments && features.ach_file_import && (
                <div
                  className="inverted-primary-color-checkbox external-account-checklist"
                  style={{ borderTop: "none", paddingBottom: "0px" }}
                >
                  <ContextForm.Field style={{ marginBottom: "0px" }}>
                    <Checkbox
                      field={MODEL_STR.ACH_PAYMENT_VIA_IMPORT}
                      label={l10n.getString(
                        "manage-users-ach-payment-with-file-import"
                      )}
                    />
                  </ContextForm.Field>
                </div>
              )}
              {canSendACHPayments && subUserLimitsPermission && (
                <div className="user-limits">
                  <ContextForm.Field
                    style={{ marginBottom: "0px" }}
                    validate={(value, allFields) =>
                      validateACHPaymentLimits(
                        value,
                        allFields,
                        MODEL_STR.ACH_PAYMENT,
                        features.limits?.unverified_ach_push?.[1]
                      )
                    }
                  >
                    <AmountTextInput
                      field="ach_payment_limits"
                      label={l10n.getString("manage-users-ach-payment-limits")}
                    />
                  </ContextForm.Field>
                </div>
              )}
              {canSendACHPayments &&
                features?.bulk_payments &&
                features?.expedited_payments &&
                features?.fednow_send && (
                  <div
                    className="inverted-primary-color-checkbox external-account-checklist"
                    style={{ borderTop: "none" }}
                  >
                    <ContextForm.Field style={{ marginBottom: "0px" }}>
                      <Checkbox
                        field={MODEL_STR.UNVERIFIED_FEDNOW_SEND}
                        label={l10n.getString("manage-users-fednow-send")}
                      />
                    </ContextForm.Field>
                    {formData[MODEL_STR.UNVERIFIED_FEDNOW_SEND] &&
                      subUserLimitsPermission && (
                        <div className="user-limits">
                          <div
                            className="alignChild--left--center"
                            style={{ gap: "var(--space-xxs)" }}
                          >
                            <ContextForm.Field
                              style={{ marginBottom: "0px" }}
                              validate={(value, allFields) =>
                                validateFedNowSendLimits(
                                  value,
                                  allFields,
                                  features.limits?.fednow_unverified?.[1]
                                )
                              }
                            >
                              <AmountTextInput
                                label={l10n.getString(
                                  "manage-users-fednow-send-daily-limits"
                                )}
                                field="fednow_unverified_send_limits"
                              />
                            </ContextForm.Field>
                          </div>
                        </div>
                      )}
                  </div>
                )}
              {canSendACHPayments && features?.user_initiated_ach_reversals && (
                <div
                  className="inverted-primary-color-checkbox external-account-checklist"
                  style={{ borderTop: "none" }}
                >
                  <ContextForm.Field style={{ marginBottom: "0px" }}>
                    <Checkbox
                      field={MODEL_STR.ACH_PAYMENT_REVERSE}
                      label={l10n.getString("manage-users-reverse-payment")}
                    />
                  </ContextForm.Field>
                </div>
              )}
            </div>
          </Feature>
          <Feature features={features} flag="ach_payments_collect">
            <div className="inverted-primary-color-checkbox">
              <div
                className="alignChild--left--center"
                style={{ gap: "var(--space-xxs)" }}
              >
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    checked={canCollectACHPayments}
                    onChange={() => {
                      const isCurrentlyChecked = canCollectACHPayments;
                      onChange(
                        achTemplatesEnabled
                          ? {
                              [MODEL_STR.COLLECT_ACH_PAYMENT]:
                                !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE]:
                                !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT]:
                                features.ach_file_import
                                  ? !isCurrentlyChecked
                                  : false,
                            }
                          : {
                              [MODEL_STR.COLLECT_ACH_PAYMENT]:
                                !isCurrentlyChecked,
                              [MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT]:
                                features.ach_file_import
                                  ? !isCurrentlyChecked
                                  : false,
                            }
                      );
                    }}
                    label={l10n.getString("manage-users-collect-ach-payment")}
                  />
                </ContextForm.Field>
              </div>
              {canCollectACHPayments &&
                (achTemplatesEnabled || features.ach_file_import) && (
                  <>
                    {achTemplatesEnabled && (
                      <div
                        className="inverted-primary-color-checkbox external-account-checklist"
                        style={{ borderTop: "none" }}
                      >
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <Checkbox
                            field={MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE}
                            label={l10n.getString(
                              "manage-users-collect-ach-payment-with-templates"
                            )}
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                    <div
                      className="inverted-primary-color-checkbox external-account-checklist"
                      style={{ borderTop: "none", paddingBottom: "0px" }}
                    >
                      <ContextForm.Field style={{ marginBottom: "0px" }}>
                        <Checkbox
                          field={MODEL_STR.COLLECT_ACH_PAYMENT}
                          label={l10n.getString(
                            "manage-users-collect-ach-payment-without-templates"
                          )}
                        />
                      </ContextForm.Field>
                    </div>
                  </>
                )}
              {canCollectACHPayments && features.ach_file_import && (
                <div
                  className="inverted-primary-color-checkbox external-account-checklist"
                  style={{ borderTop: "none", paddingBottom: "0px" }}
                >
                  <ContextForm.Field style={{ marginBottom: "0px" }}>
                    <Checkbox
                      field={MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT}
                      label={l10n.getString(
                        "manage-users-collect-ach-payment-with-file-import"
                      )}
                    />
                  </ContextForm.Field>
                </div>
              )}
              {canCollectACHPayments && subUserLimitsPermission && (
                <div className="user-limits">
                  <ContextForm.Field
                    style={{ marginBottom: "0px" }}
                    validate={(value, allFields) =>
                      validateACHPaymentLimits(
                        value,
                        allFields,
                        MODEL_STR.COLLECT_ACH_PAYMENT,
                        features.limits?.unverified_ach_pull?.[1]
                      )
                    }
                  >
                    <AmountTextInput
                      field="collect_ach_payment_limits"
                      label={l10n.getString("manage-users-ach-payment-limits")}
                    />
                  </ContextForm.Field>
                </div>
              )}
              {canCollectACHPayments &&
                features?.user_initiated_ach_reversals && (
                  <div
                    className="inverted-primary-color-checkbox external-account-checklist"
                    style={{ borderTop: "none" }}
                  >
                    <ContextForm.Field style={{ marginBottom: "0px" }}>
                      <Checkbox
                        field={MODEL_STR.ACH_PAYMENT_COLLECT_REVERSE}
                        label={l10n.getString("manage-users-reverse-payment")}
                      />
                    </ContextForm.Field>
                  </div>
                )}
            </div>
          </Feature>
          <Feature features={features} flag="ach_nacha_passthrough">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.ACH_PASSTHROUGH_SEND}
                  label={l10n.getString("manage-users-ach-passthrough-send")}
                />
              </ContextForm.Field>
              {formData[MODEL_STR.ACH_PASSTHROUGH_SEND] &&
                subUserLimitsPermission && (
                  <div className="user-limits">
                    <ContextForm.Field
                      style={{ marginBottom: "0px" }}
                      validate={(value, allFields) =>
                        validateACHPaymentLimits(
                          value,
                          allFields,
                          MODEL_STR.ACH_PASSTHROUGH_SEND,
                          features.limits?.ach_passthrough_send?.[1]
                        )
                      }
                    >
                      <AmountTextInput
                        field="ach_passthrough_send_limits"
                        label={l10n.getString(
                          "manage-users-ach-passthrough-send-limits"
                        )}
                      />
                    </ContextForm.Field>
                  </div>
                )}
            </div>
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.ACH_PASSTHROUGH_COLLECT}
                  label={l10n.getString("manage-users-ach-passthrough-collect")}
                />
              </ContextForm.Field>
              {formData[MODEL_STR.ACH_PASSTHROUGH_COLLECT] &&
                subUserLimitsPermission && (
                  <div className="user-limits">
                    <ContextForm.Field
                      style={{ marginBottom: "0px" }}
                      validate={(value, allFields) =>
                        validateACHPaymentLimits(
                          value,
                          allFields,
                          MODEL_STR.ACH_PASSTHROUGH_COLLECT,
                          features.limits?.ach_passthrough_collect?.[1]
                        )
                      }
                    >
                      <AmountTextInput
                        field="ach_passthrough_collect_limits"
                        label={l10n.getString(
                          "manage-users-ach-passthrough-collect-limits"
                        )}
                      />
                    </ContextForm.Field>
                  </div>
                )}
            </div>
          </Feature>
          <Feature features={features} flag="ach_templates">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.BULK_PAYMENT_TEMPLATE}
                  label={l10n.getString(
                    "manage-users-ach-instant-manage-templates"
                  )}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <Feature features={features} flag="ach_templates">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.APPROVE_ACH_TEMPLATES}
                  label={l10n.getString(
                    "manage-users-ach-instant-approve-templates"
                  )}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <Feature features={features} flag="international_wires">
            {internationalWireTemplatesEnabled ? (
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    checked={
                      formData[MODEL_STR.INTERNATIONAL_WIRE] ||
                      formData[MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]
                    }
                    onChange={() => {
                      const isCurrentlyChecked =
                        formData[MODEL_STR.INTERNATIONAL_WIRE] ||
                        formData[MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES];
                      onChange({
                        [MODEL_STR.INTERNATIONAL_WIRE]: !isCurrentlyChecked,
                        [MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]:
                          !isCurrentlyChecked,
                      });
                    }}
                    label={l10n.getString("manage-users-international-wire")}
                  />
                </ContextForm.Field>
                {(formData[MODEL_STR.INTERNATIONAL_WIRE] ||
                  formData[MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]) && (
                  <>
                    <div
                      className="inverted-primary-color-checkbox external-account-checklist"
                      style={{ borderTop: "none" }}
                    >
                      <ContextForm.Field style={{ marginBottom: "0px" }}>
                        <Checkbox
                          field={MODEL_STR.INTERNATIONAL_WIRE}
                          label={l10n.getString(
                            "manage-users-international-wire-send-without-templates"
                          )}
                        />
                      </ContextForm.Field>
                    </div>
                    <div
                      className="inverted-primary-color-checkbox external-account-checklist"
                      style={{ borderTop: "none", paddingBottom: "0px" }}
                    >
                      <ContextForm.Field style={{ marginBottom: "0px" }}>
                        <Checkbox
                          field={MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES}
                          label={l10n.getString(
                            "manage-users-international-wire-send-via-templates"
                          )}
                        />
                      </ContextForm.Field>
                    </div>
                    {subUserLimitsPermission && (
                      <div className="user-limits">
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <AmountTextInput
                            label={l10n.getString("manage-users-wire-limits")}
                            field="international_wire"
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.INTERNATIONAL_WIRE}
                    label={l10n.getString("manage-users-international-wire")}
                  />
                </ContextForm.Field>
                {formData[MODEL_STR.INTERNATIONAL_WIRE] &&
                  subUserLimitsPermission && (
                    <div className="user-limits">
                      <div
                        className="alignChild--left--center"
                        style={{ gap: "var(--space-xxs)" }}
                      >
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <AmountTextInput
                            label={l10n.getString("manage-users-wire-limits")}
                            field="international_wire"
                          />
                        </ContextForm.Field>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </Feature>
          <Feature features={features} flag="international_wire_templates">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES}
                  label={l10n.getString(
                    "manage-users-international-wire-manage-templates"
                  )}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <Feature features={features} flag="wires">
            {fedwireTemplatesEnabled || domesticWireImportEnabled ? (
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    checked={
                      formData[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] ||
                      (formData[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] &&
                        fedwireTemplatesEnabled) ||
                      (formData[MODEL_STR.DOMESTIC_WIRE_IMPORT] &&
                        domesticWireImportEnabled)
                    }
                    onChange={() => {
                      const isCurrentlyChecked =
                        formData[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] ||
                        (formData[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] &&
                          fedwireTemplatesEnabled) ||
                        (formData[MODEL_STR.DOMESTIC_WIRE_IMPORT] &&
                          domesticWireImportEnabled);
                      onChange({
                        [MODEL_STR.DOMESTIC_WIRE_TRANSACTION]:
                          !isCurrentlyChecked,
                        ...(fedwireTemplatesEnabled && {
                          [MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES]:
                            !isCurrentlyChecked,
                        }),
                        ...(domesticWireImportEnabled && {
                          [MODEL_STR.DOMESTIC_WIRE_IMPORT]: !isCurrentlyChecked,
                        }),
                      });
                    }}
                    label={l10n.getString("manage-users-wire_transaction")}
                  />
                </ContextForm.Field>
                {(formData[MODEL_STR.DOMESTIC_WIRE_TRANSACTION] ||
                  (formData[MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES] &&
                    fedwireTemplatesEnabled) ||
                  (formData[MODEL_STR.DOMESTIC_WIRE_IMPORT] &&
                    domesticWireImportEnabled)) && (
                  <>
                    <div
                      className="inverted-primary-color-checkbox external-account-checklist"
                      style={{ borderTop: "none" }}
                    >
                      <ContextForm.Field style={{ marginBottom: "0px" }}>
                        <Checkbox
                          field={MODEL_STR.DOMESTIC_WIRE_TRANSACTION}
                          label={l10n.getString(
                            fedwireTemplatesEnabled
                              ? "manage-users-send-without-templates"
                              : "manage-users-send-freeform-wire"
                          )}
                        />
                      </ContextForm.Field>
                    </div>
                    {fedwireTemplatesEnabled && (
                      <div
                        className="inverted-primary-color-checkbox external-account-checklist"
                        style={{ borderTop: "none" }}
                      >
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <Checkbox
                            field={MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES}
                            label={l10n.getString(
                              "manage-users-send-via-templates"
                            )}
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                    {domesticWireImportEnabled && (
                      <div
                        className="inverted-primary-color-checkbox external-account-checklist"
                        style={{ borderTop: "none" }}
                      >
                        <ContextForm.Field style={{ marginBottom: "0px" }}>
                          <Checkbox
                            field={MODEL_STR.DOMESTIC_WIRE_IMPORT}
                            label={l10n.getString(
                              "manage-users-domestic-wire-import"
                            )}
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                    {subUserLimitsPermission && (
                      <div className="user-limits">
                        <ContextForm.Field
                          style={{ marginBottom: "0px" }}
                          validate={validateWireLimits}
                        >
                          <AmountTextInput
                            field="wire_limits"
                            label={l10n.getString("manage-users-wire-limits")}
                          />
                        </ContextForm.Field>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.WIRE_TRANSACTION}
                    label={l10n.getString("manage-users-wire_transaction")}
                  />
                </ContextForm.Field>
                {formData[MODEL_STR.WIRE_TRANSACTION] &&
                  subUserLimitsPermission && (
                    <div className="user-limits">
                      <div
                        className="alignChild--left--center"
                        style={{ gap: "var(--space-xxs)" }}
                      >
                        <ContextForm.Field
                          style={{ marginBottom: "0px" }}
                          validate={validateWireLimits}
                        >
                          <AmountTextInput
                            field="wire_limits"
                            label={l10n.getString("manage-users-wire-limits")}
                          />
                        </ContextForm.Field>
                      </div>
                    </div>
                  )}
              </div>
            )}
            <Feature features={features} flag="wire_templates" equals="fedwire">
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.DOMESTIC_WIRE_TEMPLATES}
                    label={l10n.getString(
                      "manage-users-domestic-wire-manage-templates"
                    )}
                  />
                </ContextForm.Field>
              </div>
            </Feature>
          </Feature>
          <Feature features={features} flag="ach">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.ACH_TRANSFER}
                  label={l10n.getString("manage-users-ach-transfer")}
                />
              </ContextForm.Field>
              {formData[MODEL_STR.ACH_TRANSFER] && (
                <>
                  <ContextForm.Field
                    required
                    style={{ marginBottom: "0px", borderTop: "none" }}
                  >
                    <AccountCheckList
                      accessLevel={formData.accessLevel}
                      header="External Accounts"
                      field="externalAccounts"
                      containerStyle="external-account-checklist"
                    />
                  </ContextForm.Field>
                  {displayACHLimits && (
                    <div className="user-limits">
                      <ContextForm.Field
                        style={{ marginBottom: "0px" }}
                        validate={validateACHLimits}
                      >
                        <AmountTextInput
                          field="ach_limits"
                          label={l10n.getString("manage-users-ach-limits")}
                        />
                      </ContextForm.Field>
                    </div>
                  )}
                </>
              )}
            </div>
          </Feature>
          {displayAddExternalAccount && (
            // access to all external accounts (or 0 existing so far) is a prereq for this permission
            <Feature features={features} flag="ach">
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.EXTERNAL_ACCOUNT}
                    label={l10n.getString("manage-users-external-account")}
                  />
                </ContextForm.Field>
              </div>
            </Feature>
          )}
          <Feature features={features} flag="zelle">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.ZELLE}
                  label={l10n.getString("manage-users-zelle")}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <div className="inverted-primary-color-checkbox">
            <div
              className="alignChild--left--center"
              style={{ gap: "var(--space-xxs)" }}
            >
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.RECIPIENT}
                  label={l10n.getString("manage-users-recipient")}
                />
              </ContextForm.Field>
            </div>
          </div>
          {businessBillpayEnabled && (
            <>
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.PAYMENT}
                    label={l10n.getString("manage-users-payment")}
                  />
                </ContextForm.Field>
              </div>
              {formData[MODEL_STR.PAYMENT] && (
                <div
                  className="inverted-primary-color-checkbox"
                  style={{
                    borderTop: "none",
                    paddingTop: "0px",
                    margin: "0 var(--space-xl)",
                  }}
                >
                  <ContextForm.Field style={{ marginBottom: "0px" }}>
                    <Checkbox
                      field={MODEL_STR.PAYEE}
                      label={l10n.getString("manage-users-payee")}
                    />
                  </ContextForm.Field>
                </div>
              )}
            </>
          )}
          <Feature features={features} flag="rdc">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.RDC}
                  label={l10n.getString("manage-users-rdc")}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <div className="inverted-primary-color-checkbox">
            <ContextForm.Field style={{ marginBottom: "0px" }}>
              <Checkbox
                field={MODEL_STR.DUAL_APPROVAL_REQUEST}
                label={l10n.getString("manage-users-dual-approval-request")}
              />
            </ContextForm.Field>
            <div className="inverted-primary-color-checkbox external-account-checklist bottom-notice fontSize--s">
              <span className="narmi-icon-alert-circle" />
              <span className="margin--left--xs">
                {l10n.getString("manage-users-dual-approval-permission-notice")}
              </span>
              <a className="margin--left--xs" href="/settings/security">
                {l10n.getString("manage-users-dual-approval-settings-link")}
              </a>
            </div>
          </div>
          {formData[MODEL_STR.ACCOUNT] ? (
            <Feature features={features} flag="cards">
              <div className="inverted-primary-color-checkbox">
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <Checkbox
                    field={MODEL_STR.CARD}
                    label={l10n.getString("manage-users-card")}
                  />
                </ContextForm.Field>
              </div>
            </Feature>
          ) : null}{" "}
          <Feature features={features} flag="ach_file_import">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.ACH_IMPORT_FILE_DOWNLOADS}
                  label={l10n.getString(
                    "manage-users-ach-import-file-downloads"
                  )}
                />
              </ContextForm.Field>
            </div>
          </Feature>
          <Feature features={features} flag="business_banking_api">
            <div className="inverted-primary-color-checkbox">
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <Checkbox
                  field={MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES}
                  label={l10n.getString("org-role-perm-create-api-tokens")}
                />
              </ContextForm.Field>
            </div>
          </Feature>
        </div>
        {/* .permission-check-list */}
        {formData[MODEL_STR.ACCOUNT] ? (
          <Feature features={features} flag="statements">
            <>
              <h5 className="title-style account-activity-title-style fontWeight--bold">
                Account Activity
              </h5>
              <div className="permission-check-list">
                <div className="inverted-primary-color-checkbox">
                  <ContextForm.Field style={{ marginBottom: "0px" }}>
                    <Checkbox
                      field={MODEL_STR.DOCUMENTS}
                      label={l10n.getString("manage-users-documents")}
                    />
                  </ContextForm.Field>
                </div>
              </div>
            </>
          </Feature>
        ) : null}
      </SectionCard>
      <InstitutionSettingsContextProvider>
        <SubUserLimitsDialog
          userWireLimits={subUsersWithWireLimits}
          userACHLimits={subUsersWithACHLimits}
          userUnverifiedACHLimits={subUsersWithUnverifiedACHLimits}
          userInternationalWireLimits={subUsersWithInternationalWireLimits}
          userCollectACHLimits={subUsersWithCollectACHLimits}
          userUnverifiedFednowLimits={subUsersWithUnverifiedFednowLimits}
          userACHPassthroughSendLimits={subUsersWithACHPassthroughSendLimits}
          userACHPassthroughCollectLimits={
            subUsersWithACHPassthroughCollectLimits
          }
          isOpen={isLimitsDialogOpen}
          onClose={toggleLimitsDialog}
          features={features}
        />
      </InstitutionSettingsContextProvider>
      <Error error={error} />
    </div>
  );
}

OrganizationAccessLevelExtraPermissions.propTypes = {
  error: PropTypes.string,
  features: PropTypes.object,
  organizationUsers: PropTypes.array,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  isUsingConsumerBillpay: PropTypes.bool,
};

OrganizationAccessLevelExtraPermissions.affectedPermissions = [
  MODEL_STR.INTRA_INSTITUTION_TRANSFER,
  MODEL_STR.ACH_TRANSFER,
  MODEL_STR.BULK_PAYMENT_TEMPLATE,
  MODEL_STR.EXTERNAL_ACCOUNT,
  MODEL_STR.WIRE_TRANSACTION,
  MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
  MODEL_STR.DOMESTIC_WIRE_TEMPLATES,
  MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
  MODEL_STR.PAYMENT,
  MODEL_STR.PAYEE,
  MODEL_STR.DUAL_APPROVAL_REQUEST,
  MODEL_STR.APPROVE_ACH_TEMPLATES,
  MODEL_STR.RDC,
  MODEL_STR.CARD,
  MODEL_STR.DOCUMENTS,
  MODEL_STR.ACCOUNT,
  MODEL_STR.ACH_PAYMENT,
  MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE,
  MODEL_STR.ACH_PAYMENT_VIA_IMPORT,
  MODEL_STR.ACH_IMPORT_FILE_DOWNLOADS,
  MODEL_STR.COLLECT_ACH_PAYMENT,
  MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
  MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT,
  MODEL_STR.UNVERIFIED_FEDNOW_SEND,
  MODEL_STR.ACH_PAYMENT_REVERSE,
  MODEL_STR.ACH_PAYMENT_COLLECT_REVERSE,
  MODEL_STR.RECIPIENT,
  MODEL_STR.ZELLE,
  MODEL_STR.INTERNATIONAL_WIRE,
  MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES,
  MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
  MODEL_STR.DOMESTIC_WIRE_IMPORT,
  MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES,
  MODEL_STR.ACH_PASSTHROUGH_SEND,
  MODEL_STR.ACH_PASSTHROUGH_COLLECT,
];

OrganizationAccessLevelExtraPermissions.affectedPermissionsRequiringAllAccounts =
  [MODEL_STR.CARD, MODEL_STR.DOCUMENTS];
